import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { Subject } from 'rxjs'
import { filter, take, takeUntil } from 'rxjs/operators'
import { AppRoutingConfig } from '../../configuration/app.routing.config'
import { Structure } from '../../models/dev.test.models'
import { ErrorService } from '../../services/error.service'
import { KeyingDataSetsService } from '../../services/keying.data.sets.service'
import { KeyingSystemService } from '../../services/keying.system.service'
import { LoadingService } from '../../services/loading.service'
import { LoggingService } from '../../services/logging.service'

@Component({
  selector: 'app-generate-tmk-dialog',
  templateUrl: './generate-tmk-dialog.component.html',
  styleUrls: ['./generate-tmk-dialog.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenerateTmkDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  generateTmkForm: FormGroup
  request
  tmkDisabled = false
  otherDisabled = false
  formDisabled = true
  keySystem = {
    topMasterKey: [],
    parity: [],
    keyPatternOrder: [],
    sequenceOfProgression: [],
  }

  ignoreGuidelines = false
  deviceChamber = -1
  focus = {
    deviceChamber: false,
  }

  formError = null
  changesMade = false

  /* istanbul ignore next */
  @HostListener('document:keyup.enter', ['$event']) onEnterHandler(event: KeyboardEvent) {
    this.generate()
  }

  constructor(
    public dialogRef: MatDialogRef<GenerateTmkDialogComponent>,
    public form: FormBuilder,
    private keyingDataSetsService: KeyingDataSetsService,
    private keyingSystemService: KeyingSystemService,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private loggingService: LoggingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.keyingDataSetsService.keySystemDataSets$.pipe(takeUntil(this.destroy$)).subscribe((keySystemDataSets) => {
      if (!keySystemDataSets) {
        return
      } else if (keySystemDataSets.keySystem && keySystemDataSets.keySystem.topMasterKey) {
        this.keySystem.topMasterKey = keySystemDataSets.keySystem.topMasterKey.split('')
        this.keySystem.parity = keySystemDataSets.keySystem.parity.split('')
        this.keySystem.keyPatternOrder = keySystemDataSets.keySystem.keyPatternOrder.split('')
        this.keySystem.sequenceOfProgression = keySystemDataSets.keySystem.sequenceOfProgression.split('')

        // If TMK values are populated but Ascending/Descending isn't complete, calculate it
        const hasEmptyKeyPatternValues = this.keySystem.keyPatternOrder.some((val) => !val || val === '')
        if (hasEmptyKeyPatternValues && this.keySystem.topMasterKey.some((val) => val !== null && val !== '')) {
          this.populateAscendingDescending()
        }
      } else {
        this.keySystem.topMasterKey = []
        this.keySystem.parity = []
        this.keySystem.keyPatternOrder = []
        this.keySystem.sequenceOfProgression = []

        let chambers = []
        switch (this.data.structure.chamberTypeId) {
          case 1:
            chambers = [null, null, null, null, null]
            break
          case 2:
            chambers = [null, null, null, null, null, null]
            break
          case 3:
            chambers = [null, null, null, null, null, null, null]
            break
          default:
            break
        }

        this.keySystem.topMasterKey = chambers.map((c) => c)
        this.keySystem.parity = chambers.map((c) => c)
        this.keySystem.keyPatternOrder = chambers.map((c) => c)
        this.keySystem.sequenceOfProgression = chambers.map((c) => c)
      }
      this.cdr.markForCheck()
    })
  }

  canSubmit() {
    if (
      !this.keySystem.topMasterKey.some((bit) => bit === null || bit === '') &&
      !this.keySystem.parity.some((bit) => bit === null || bit === '') &&
      !this.keySystem.keyPatternOrder.some((bit) => bit === null || bit === '') &&
      !this.keySystem.sequenceOfProgression.some(
        (bit) => bit === null || bit === '' || this.checkIfDuplicateExists(this.keySystem.sequenceOfProgression)
      ) &&
      !this.keySystem.parity.some((bit) => bit === null || bit === '') &&
      this.changesMade
    ) {
      return false
    }
    return true
  }

  /* istanbul ignore next */
  checkIfDuplicateExists(arr) {
    return new Set(arr).size !== arr.length
  }

  update(subject?) {
    let formatted

    if (
      !subject ||
      subject === 'topMasterKey' ||
      subject === 'keyPatternOrder' ||
      subject === 'sequenceOfProgression'
    ) {
      this.calcParity()

      // Auto-populate the Ascending/Descending values based on TMK input
      if (subject === 'topMasterKey') {
        this.populateAscendingDescending()
      }

      this.tmkDisabled = false
      this.otherDisabled = true
      this.formDisabled = false
      formatted = {
        topMasterKey: this.keySystem.topMasterKey.join('').toLocaleUpperCase(),
        parity: this.keySystem.parity.join('').toLocaleUpperCase(),
        keyPatternOrder: this.keySystem.keyPatternOrder.join('').toLocaleUpperCase(),
        sequenceOfProgression: this.keySystem.sequenceOfProgression.join('').toLocaleUpperCase(),
      }
      this.request = [
        {
          operationType: 'replace',
          path: '/parity',
          value: formatted.parity,
        },
        {
          operationType: 'replace',
          path: '/topMasterKey',
          value: formatted.topMasterKey,
        },
        {
          operationType: 'replace',
          path: '/keyPatternOrder',
          value: formatted.keyPatternOrder,
        },
        {
          operationType: 'replace',
          path: '/sequenceOfProgression',
          value: formatted.sequenceOfProgression,
        },
        {
          operationType: 'replace',
          path: '/ignoreErrors',
          value: this.ignoreGuidelines,
        },
      ]
      if (this.data?.isRCK) {
        this.request.push({
          operationType: 'replace',
          path: '/lostBallChamber',
          value: this.deviceChamber,
        })
      }
    } else {
      this.tmkDisabled = true
      this.otherDisabled = false
      this.formDisabled = false
      formatted = {
        parity: this.keySystem.parity.join('').toLocaleUpperCase(),
        keyPatternOrder: this.keySystem.keyPatternOrder.join('').toLocaleUpperCase(),
        sequenceOfProgression: this.keySystem.sequenceOfProgression.join('').toLocaleUpperCase(),
      }
      this.request = [
        {
          operationType: 'replace',
          path: '/parity',
          value: formatted.parity,
        },
        {
          operationType: 'replace',
          path: '/keyPatternOrder',
          value: formatted.keyPatternOrder,
        },
        {
          operationType: 'replace',
          path: '/sequenceOfProgression',
          value: formatted.sequenceOfProgression,
        },
        {
          operationType: 'replace',
          path: '/ignoreErrors',
          value: this.ignoreGuidelines,
        },
      ]
      if (this.data?.isRCK) {
        this.request.push({
          operationType: 'replace',
          path: '/lostBallChamber',
          value: this.deviceChamber,
        })
      }
    }
    this.changesMade = true
    this.loggingService.log('Request:', this.request)
  }

  populateAscendingDescending() {
    const isA2Rule = this.data.structure.combinatingRuleSetId === 6
    const isA4Rule = this.data.structure.combinatingRuleSetId === 7
    const length = this.keySystem.topMasterKey.length

    // Initialize the keyPatternOrder array if it's empty
    if (!this.keySystem.keyPatternOrder || this.keySystem.keyPatternOrder.some((v) => v === null)) {
      this.keySystem.keyPatternOrder = Array(length).fill('')
    }

    // Set Bow and Tip positions based on the rule
    const bowIndex = isA2Rule || isA4Rule ? length - 1 : 0
    const tipIndex = isA2Rule || isA4Rule ? 0 : length - 1

    // Process all positions including Bow and Tip
    for (let i = 0; i < length; i++) {
      const tmkValue = this.keySystem.topMasterKey[i]

      // If TMK value is empty or null, make corresponding Ascending/Descending value blank
      if (tmkValue === null || tmkValue === '') {
        this.keySystem.keyPatternOrder[i] = ''
        continue
      }

      // Bow is always Ascending
      if (i === bowIndex) {
        this.keySystem.keyPatternOrder[i] = 'A'
      }
      // Tip is always Descending
      else if (i === tipIndex) {
        this.keySystem.keyPatternOrder[i] = 'D'
      }
      // For intermediate positions, set based on TMK value
      else {
        const value = parseInt(tmkValue, 10)

        // Ensure the value is a number before processing
        if (!isNaN(value)) {
          // 0-4 = A, 5-9 = D
          if (isA4Rule) {
            this.keySystem.keyPatternOrder[i] = value >= 0 && value <= 2 ? 'A' : 'D'
          } else {
            this.keySystem.keyPatternOrder[i] = value >= 0 && value <= 4 ? 'A' : 'D'
          }
        }
      }
    }
  }

  calcParity() {
    this.keySystem.topMasterKey.forEach((bit, index) => {
      if (bit && bit % 2 === 0) {
        this.keySystem.parity[index] = 'E'
      } else if (bit) {
        this.keySystem.parity[index] = 'O'
      }
    })
  }

  /* istanbul ignore next */
  generate() {
    this.patchRckTmk(this.request)
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  close() {
    this.dialogRef.close()
    this.router.navigate([AppRoutingConfig.getStructureHierarchyUrl(this.data.structure.id, this.data.keySystem.id)])
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  patchRckTmk(request) {
    this.keyingSystemService
      .patchRckTmk(this.data.structure.id, this.data.keySystem.id, request, this.data.isRCK)
      .pipe(take(1))
      .subscribe(
        () => {
          this.formError = null
          this.cdr.markForCheck()
          this.dialogRef.close()
          this.loadingService.set({ isLoading: false, message: '' })
          this.keyingDataSetsService.loadKeySystemDataSets(this.data.structure.id, this.data.keySystem.id)
          this.keyingDataSetsService.keySystemDataSets$.pipe(
            filter((data) => !!(data?.keySystem?.topMasterKey)),
            take(1)
          ).subscribe(() => {
            this.router.navigate([AppRoutingConfig.getStructureHierarchyUrl(this.data.structure.id, this.data.keySystem.id)])
          })
        },
        (error) => {
          this.formError = error.error.message
          this.cdr.markForCheck()
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }
}
